import type { EnhancedScopeData } from '@ardoq/data-model';
import type { BlocksArrangement } from './arrangementRecipe';
import type { Locale } from '@ardoq/locale';
import {
  type ArrangeableNode,
  type ResolvedArrangement,
  resolveArrangement,
  toChildArrangements,
} from './compileArrangement';
import {
  type ArrangeableTreeNode,
  findNode,
  toArrangeableNodes,
} from './arrangeableNodes';

export const childrenOf = (
  scopeData: EnhancedScopeData,
  root: ArrangeableTreeNode,
  containerId: string | null
): ArrangeableNode[] | null => {
  const container = containerId === null ? root : findNode(root, containerId);
  return container?.children
    ? toArrangeableNodes(scopeData, container.children)
    : null;
};

type ResolveContainerArrangementsArgs = {
  scopeData: EnhancedScopeData;
  root: ArrangeableTreeNode;
  locale: Locale;
  arrangements: BlocksArrangement[];
};

/** How the compiler lays out each arrangement whose group is still in the view. */
export const resolveContainerArrangements = ({
  scopeData,
  root,
  locale,
  arrangements,
}: ResolveContainerArrangementsArgs): ResolvedArrangement[] =>
  arrangements.flatMap(arrangement => {
    const children = childrenOf(scopeData, root, arrangement.containerId);
    return children
      ? [resolveArrangement({ arrangement, children, locale })]
      : [];
  });

const derivedFrom = (
  scopeData: EnhancedScopeData,
  root: ArrangeableTreeNode,
  arrangement: BlocksArrangement
): BlocksArrangement[] => {
  const children = childrenOf(scopeData, root, arrangement.containerId);
  return children ? toChildArrangements(arrangement, children) : [];
};

/**
 * One arrangement per group, adding those a region's `childLayout` implies. A later arrangement
 * wins, and an implied one takes the place of the arrangement it came from.
 */
export const withChildArrangements = (
  scopeData: EnhancedScopeData,
  root: ArrangeableTreeNode,
  arrangements: BlocksArrangement[]
): BlocksArrangement[] => [
  ...new Map(
    arrangements
      .flatMap(arrangement => [
        arrangement,
        ...derivedFrom(scopeData, root, arrangement),
      ])
      .map(arrangement => [arrangement.containerId, arrangement] as const)
  ).values(),
];

/** The stored arrangements still in effect: those no later arrangement or `childLayout` overrides. */
export const effectiveArrangements = (
  scopeData: EnhancedScopeData,
  root: ArrangeableTreeNode,
  arrangements: BlocksArrangement[]
): BlocksArrangement[] => {
  const resolved = new Set(
    withChildArrangements(scopeData, root, arrangements)
  );
  return arrangements.filter(arrangement => resolved.has(arrangement));
};
